import { t } from "@/lib/i18n"

// Single source of truth for staff-facing status marks. Every dot/segment in
// /dashboard, /tables and /tables/:id must resolve through these maps so the
// same state never changes color between screens (tokens from application.css).
export type OperationalStatus =
  | "free"
  | "seated"
  | "ordering"
  | "cooking"
  | "ready"
  | "paying"
  /** Paid up and empty, waiting on staff to clear it. Not free until they do. */
  | "awaiting_release"
  | "reserved"
  | "disabled"

// The menu and the bill are service surfaces: they only mean something once the
// table is actually in service. `free` and `reserved` have nobody to charge yet,
// and `disabled` cannot take orders at all — showing a full menu there invites
// the waiter to build an order the table can't hold. Everything else counts as
// occupied, including `awaiting_release`: the table is still `occupied` in the
// DB and its closed bills have to stay reachable.
const UNOCCUPIED_OPERATIONAL_STATUSES = new Set<OperationalStatus>([
  "free",
  "reserved",
  "disabled",
])

export function tableIsOccupied(status: OperationalStatus): boolean {
  return !UNOCCUPIED_OPERATIONAL_STATUSES.has(status)
}

/**
 * Why a table is asking for attention. Mirrors
 * Staff::TablesWorkspaceSerializer::ALERT_REASONS_BY_PRIORITY — the server sends
 * these already ordered, but the front re-sorts rather than trusting arrival
 * order, so a payload change can never silently downgrade what staff reads first.
 */
export type AlertReason =
  | "entry_requested"
  | "waiter_requested"
  | "ready_unserved"
  | "payment_pending"
  | "awaiting_release"

// Most urgent first: a person who cannot sit down beats cold food, which beats
// a waiter call from someone already seated, which beats money sitting on the
// table, which beats a table that just needs wiping. Mirrors
// `Staff::TablesWorkspaceSerializer::ALERT_REASONS_BY_PRIORITY`.
export const ALERT_REASONS_BY_PRIORITY: AlertReason[] = [
  "entry_requested",
  "waiter_requested",
  "ready_unserved",
  "payment_pending",
  "awaiting_release",
]

const ALERT_REASON_MESSAGE_KEYS: Record<AlertReason, string> = {
  entry_requested: "ui.staff.tables.alerts.entry_requested",
  waiter_requested: "ui.staff.tables.alerts.waiter_requested",
  ready_unserved: "ui.staff.tables.alerts.ready_unserved",
  payment_pending: "ui.staff.tables.alerts.payment_pending",
  awaiting_release: "ui.staff.tables.alerts.awaiting_release",
}

export function primaryAlertReason(reasons: AlertReason[] = []): AlertReason | null {
  return ALERT_REASONS_BY_PRIORITY.find((reason) => reasons.includes(reason)) ?? null
}

/** The message for the most urgent reason, or null when the table is quiet. */
export function alertMessageFor(reasons: AlertReason[] = []): string | null {
  const reason = primaryAlertReason(reasons)
  if (!reason) return null

  return t(ALERT_REASON_MESSAGE_KEYS[reason])
}

/**
 * The attention pulse. `motion-safe:` IS the prefers-reduced-motion contract:
 * when the viewer asks for less motion the class never applies, and the static
 * destructive dot/icon underneath is the fallback — there is no second rule to
 * keep in sync, and nothing disappears when the animation does.
 */
export const ALERT_PULSE_CLASS = "motion-safe:animate-pulse"

// `Location#feature_enabled?(:dynamic_invites)`, mirrored client-side to gate
// every waiter-issued-QR control: the printed static QR is the MVP's default
// door, and generating a temporary one is now the opt-in, off by default.
export const DYNAMIC_INVITES_FEATURE = "dynamic_invites"

export type StaffMutationResource = "table" | "bill" | "attempt"

export function staffMutationResourceKey(
  resource: StaffMutationResource,
  id: number | string
): string {
  return `${resource}:${id}`
}

export const TABLE_STATUS_DOTS: Record<string, string> = {
  available: "bg-status-available",
  occupied: "bg-status-occupied",
  reserved: "bg-status-reserved",
  disabled: "bg-status-disabled/40",
}

// Operational states collapse to their table-status family: green means a
// table is free everywhere; order-level urgency (ready, alerts) renders as a
// separate mark, never by recoloring the table dot.
export const OPERATIONAL_STATUS_DOTS: Record<OperationalStatus, string> = {
  free: "bg-status-available",
  seated: "bg-status-occupied",
  ordering: "bg-status-occupied",
  cooking: "bg-status-occupied",
  ready: "bg-status-occupied",
  paying: "bg-status-occupied",
  // Deliberately outside the occupied family: nobody is sitting there and nothing
  // is owed. The dot reads "no blockers", the label says what is still pending.
  awaiting_release: "bg-status-available",
  reserved: "bg-status-reserved",
  disabled: "bg-status-disabled/40",
}

const OPERATIONAL_STATUS_LABEL_KEYS: Record<OperationalStatus, string> = {
  free: "ui.react.features.staff.tables_overview.free",
  seated: "ui.react.features.staff.tables_overview.occupied",
  ordering: "ui.react.features.staff.tables_overview.taking_order",
  cooking: "ui.react.features.staff.tables_overview.kitchen",
  ready: "ui.react.features.staff.tables_overview.ready_serve",
  paying: "ui.react.features.staff.tables_overview.charge",
  awaiting_release: "ui.react.features.staff.tables_overview.awaiting_release",
  reserved: "enums.location.table.status.reserved",
  disabled: "enums.location.table.status.disabled",
}

export const BILL_STATUS_DOTS: Record<string, string> = {
  open: "border border-muted-foreground/50 bg-transparent",
  closed: "bg-status-available",
  cancelled: "bg-muted-foreground/40",
}

export function tableStatusDot(status: string): string {
  return TABLE_STATUS_DOTS[status] ?? "bg-muted-foreground/30"
}

export function operationalStatusDot(status: OperationalStatus): string {
  return OPERATIONAL_STATUS_DOTS[status] ?? "bg-muted-foreground/30"
}

export function operationalStatusLabelKey(status: OperationalStatus): string {
  return OPERATIONAL_STATUS_LABEL_KEYS[status]
}

export function billStatusDot(status: string): string {
  return BILL_STATUS_DOTS[status] ?? "bg-muted-foreground/30"
}
